import { Divider } from "@/kit/ui/divider";
import { intersperse } from "@/utils/helpers";
import { cn } from "@/utils/tailwind";
import {
  Children,
  ComponentPropsWithoutRef,
  ComponentType,
  ReactNode,
} from "react";
import { EmptyFallback } from "./EmptyFallback";
import { IconProps } from "@/icons";

function Divided({ children }: { children: ReactNode[] }) {
  return intersperse(
    (index) => <Divider key={`divider-${index}`} className="w-full" />,
    Children.toArray(children),
  );
}

export interface DividedItemProps extends Omit<
  ComponentPropsWithoutRef<"div">,
  "title"
> {
  title: ReactNode;
  description?: ReactNode;
}

function DividedItem({
  children,
  className,
  description,
  title,
  ...props
}: DividedItemProps) {
  return (
    <div
      {...props}
      className={cn("flex flex-col items-start justify-start gap-2", className)}
    >
      <p className="text-grey-700 font-semibold text-sm leading-tight">
        {title}
      </p>

      <EmptyFallback
        fallback={
          description && (
            <p className="text-grey-600 font-normal text-sm leading-tight">
              {description}
            </p>
          )
        }
      >
        {children}
      </EmptyFallback>
    </div>
  );
}

function VDivider() {
  return (
    <div className="h-1 w-1 relative">
      <div className="w-1 h-1 left-0 top-0 absolute bg-grey-600 rounded-full shrink-0" />
    </div>
  );
}

function VDivided({
  children,
  className,
  ...props
}: ComponentPropsWithoutRef<"div">) {
  return (
    <div
      {...props}
      className={cn(
        "inline-flex items-center justify-start sm:gap-3 gap-2 overflow-hidden",
        className,
      )}
    >
      {intersperse(
        (index) => (
          <VDivider key={`divider${index}`} />
        ),
        Children.toArray(children),
      )}
    </div>
  );
}

export interface VDividedItemProps extends Omit<
  ComponentPropsWithoutRef<"div">,
  "title"
> {
  icon: ComponentType<IconProps>;
  title?: ReactNode;
}

function VDividedItem({
  children,
  className,
  icon: Icon,
  title,
  ...props
}: VDividedItemProps) {
  return (
    <div
      {...props}
      className={cn("inline-flex justify-start items-center gap-1", className)}
    >
      <Icon className="text-grey-600 sm:w-5 w-3 sm:h-5 h-3 shrink-0" />
      <p className="text-grey-600 text-sm font-normal leading-tight flex items-center gap-1">
        {title ? (
          <>
            <span className="hidden sm:block shrink-0">{title}</span>
            <span className="whitespace-nowrap">{children}</span>
          </>
        ) : (
          children
        )}
      </p>
    </div>
  );
}

export { DividedItem, Divided, VDividedItem, VDivided };
